// Alarmes: os ativos agora (de todo o escopo) e o histórico com duração de cada ocorrência.

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Badge, Button, Card, EmptyState, PageHeader, Select, Spinner, Table, cx } from "../components/ui";
import { type ActiveAlarm, type AlarmHistoryRow, api } from "../lib/api";
import { useTree } from "../lib/hooks";

const PERIODS = [
  { key: "24h", label: "24 horas", ms: 86400_000 },
  { key: "7d", label: "7 dias", ms: 7 * 86400_000 },
  { key: "30d", label: "30 dias", ms: 30 * 86400_000 },
];

export function duration(fromIso: string, toIso: string | null, now = Date.now()): string {
  const ms = (toIso ? Date.parse(toIso) : now) - Date.parse(fromIso);
  if (ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${s % 60} s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

export function AlarmsPage() {
  const { data: tree } = useTree();
  const [period, setPeriod] = useState("24h");
  const [regulatorId, setRegulatorId] = useState<string>("");
  const p = PERIODS.find((x) => x.key === period)!;

  const regs = useMemo(
    () =>
      tree?.groups.flatMap((g) =>
        g.sites.flatMap((s) => s.units.flatMap((u) => u.regulators.map((r) => ({ id: r.id, label: `${s.name} › ${u.name} › ${r.name}` })))),
      ) ?? [],
    [tree],
  );

  const active = useQuery({
    queryKey: ["alarms", "active"],
    queryFn: () => api.get<{ alarms: ActiveAlarm[] }>("/api/alarms/active"),
    refetchInterval: 30_000,
  });
  const history = useQuery({
    queryKey: ["alarms", "history", period, regulatorId],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - p.ms);
      const q = `from=${from.toISOString()}&to=${to.toISOString()}${regulatorId ? `&regulatorId=${regulatorId}` : ""}`;
      return api.get<{ events: AlarmHistoryRow[] }>(`/api/alarms/history?${q}`);
    },
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <PageHeader title="Alarmes" description="Alarmes ativos agora e o histórico de ocorrências." />

      <section className="mb-8">
        <h2 className="mb-3 font-semibold">Ativos agora</h2>
        {active.isLoading ? (
          <Spinner />
        ) : !active.data?.alarms.length ? (
          <Card className="flex items-center gap-2 p-4 text-sm text-muted">
            <CheckCircle2 className="size-4 text-ok" aria-hidden /> Nenhum alarme ativo.
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Alarme</th>
                <th>Onde</th>
                <th>Desde</th>
                <th>Há</th>
              </tr>
            </thead>
            <tbody>
              {active.data.alarms.map((a) => (
                <tr key={`${a.regulator_id}.${a.word}.${a.bit}`}>
                  <td>
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="size-4 shrink-0 text-danger" aria-hidden />
                      <span>
                        <span className="font-medium">{a.description}</span>
                        <code className="ml-2 font-mono text-xs text-muted">{a.tag}</code>
                      </span>
                    </span>
                  </td>
                  <td className="text-sm">
                    <Link to={`/reguladores/${a.regulator_id}`} className="hover:underline">
                      {a.site_name} › {a.unit_name} › {a.regulator_name}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap text-xs text-muted">{new Date(a.since).toLocaleString("pt-BR")}</td>
                  <td className="whitespace-nowrap text-sm tabular-nums">{duration(a.since, null)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="mr-2 font-semibold">Histórico</h2>
          <div className="inline-flex rounded-md border border-line bg-surface p-0.5" role="group" aria-label="Período">
            {PERIODS.map((x) => (
              <button
                key={x.key}
                type="button"
                onClick={() => setPeriod(x.key)}
                aria-pressed={x.key === period}
                className={cx("rounded px-3 py-1.5 text-sm", x.key === period ? "bg-brand text-white" : "text-muted hover:bg-surface-2 hover:text-fg")}
              >
                {x.label}
              </button>
            ))}
          </div>
          <Select aria-label="Regulador" value={regulatorId} onChange={(e) => setRegulatorId(e.target.value)} className="w-auto! min-w-56">
            <option value="">Todos os reguladores</option>
            {regs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
          {history.data && history.data.events.length > 0 && (
            <Button size="sm" variant="secondary" className="ml-auto" onClick={() => downloadCsv(history.data!.events)}>
              <Download className="size-3.5" aria-hidden /> CSV
            </Button>
          )}
        </div>
        {history.isLoading ? (
          <Spinner />
        ) : !history.data?.events.length ? (
          <EmptyState title="Nenhum alarme no período" />
        ) : (
          <div className={cx("transition-opacity", history.isFetching && "opacity-80")}>
            <Table>
              <thead>
                <tr>
                  <th>Ativou</th>
                  <th>Normalizou</th>
                  <th>Duração</th>
                  <th>Alarme</th>
                  <th>Onde</th>
                </tr>
              </thead>
              <tbody>
                {history.data.events.map((e, i) => (
                  <tr key={i}>
                    <td className="whitespace-nowrap text-xs tabular-nums">{new Date(e.activated_at).toLocaleString("pt-BR")}</td>
                    <td className="whitespace-nowrap text-xs tabular-nums">
                      {e.cleared_at ? new Date(e.cleared_at).toLocaleString("pt-BR") : <Badge tone="danger">ainda ativo</Badge>}
                    </td>
                    <td className="whitespace-nowrap text-sm tabular-nums">{duration(e.activated_at, e.cleared_at)}</td>
                    <td>
                      <span className="font-medium">{e.description}</span>
                      <code className="ml-2 font-mono text-xs text-muted">{e.tag}</code>
                    </td>
                    <td className="text-sm text-muted">
                      {e.site_name} › {e.unit_name} › {e.regulator_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </section>
    </>
  );
}

function downloadCsv(rows: AlarmHistoryRow[]) {
  const header = ["ativou", "normalizou", "duracao_s", "tag", "alarme", "usina", "ug", "regulador"];
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = rows.map((r) =>
    [
      r.activated_at,
      r.cleared_at ?? "",
      r.cleared_at ? String(Math.round((Date.parse(r.cleared_at) - Date.parse(r.activated_at)) / 1000)) : "",
      r.tag,
      esc(r.description),
      esc(r.site_name),
      esc(r.unit_name),
      esc(r.regulator_name),
    ].join(";"),
  );
  const blob = new Blob(["﻿" + [header.join(";"), ...lines].join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `alarmes-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
